import type { ReactNode } from "react"
import {
	IconCircleInfoOutline18 as Info,
	IconPlusOutline18 as Plus,
} from "nucleo-ui-outline-18"

import type { FieldRule } from "@productlane/lib"
import { Button } from "@productlane/ui"

import type { RefsState } from "../model/refs-state"
import type { SecureRefOption } from "../model/secure-ref-picker"
import type { SchemaField } from "../model/tool-schema-fields"
import { FieldRow } from "../fields/field-row"

interface Props {
	/** Keys the rows per configuration, so per-field editor state resets with the tab. */
	variantId: string
	schemaFields: readonly SchemaField[]
	schemaUnavailable: boolean
	extraPaths: readonly string[]
	ruleFor: (path: string) => FieldRule
	shadowedPaths: Map<string, string>
	knownKeysByPath: Map<string, string[]>
	refOptions: SecureRefOption[]
	refsState: RefsState
	onRetryRefs: () => void
	refBudgetLeft: number
	onChangeRule: (path: string, rule: FieldRule | null, save?: boolean) => void
	onAddArgument: () => void
}

/** The tool's own arguments, then the ones added by hand. */
export function FieldList({
	variantId,
	schemaFields,
	schemaUnavailable,
	extraPaths,
	ruleFor,
	shadowedPaths,
	knownKeysByPath,
	refOptions,
	refsState,
	onRetryRefs,
	refBudgetLeft,
	onChangeRule,
	onAddArgument,
}: Props) {
	const shared = { refOptions, refsState, onRetryRefs, refBudgetLeft }

	return (
		<>
			{schemaFields.length === 0 ? (
				schemaUnavailable ? (
					<div className="mx-1 my-2 flex items-start gap-2 rounded-lg bg-gray-100 p-2.5 dark:bg-gray-800">
						<Info className="mt-0.5 h-3.5 w-3.5 flex-none text-tertiary" />
						<span className="block text-xs">
							<span className="block font-medium text-primary">
								This server advertises no schema for this tool
							</span>
							<span className="mt-0.5 block text-secondary">
								Add the argument paths by hand below. The rules apply the same
								way.
							</span>
						</span>
					</div>
				) : (
					<p className="px-1 py-2 text-xs text-tertiary">
						This tool takes no arguments, so there is nothing to guard.
					</p>
				)
			) : null}

			{schemaFields.length > 0 ? (
				<SectionHeading>Defined fields</SectionHeading>
			) : null}

			{schemaFields.map((field) => (
				<FieldRow
					key={`${variantId}:${field.path}`}
					field={field}
					rule={ruleFor(field.path)}
					onChange={(rule, save) => onChangeRule(field.path, rule, save)}
					knownKeys={knownKeysByPath.get(field.path)}
					shadowedBy={shadowedPaths.get(field.path)}
					{...shared}
				/>
			))}

			{extraPaths.length > 0 ? (
				<SectionHeading>Custom fields</SectionHeading>
			) : null}

			{extraPaths.map((path) => (
				<FieldRow
					key={`${variantId}:${path}`}
					field={{
						path,
						name: path,
						depth: 0,
						type: "custom path",
						// No schema to read, so nothing constrains the value.
						kinds: [],
						nullable: false,
						required: false,
						kind: "leaf",
					}}
					rule={ruleFor(path)}
					onChange={(rule, save) => onChangeRule(path, rule, save)}
					onRemove={() => onChangeRule(path, null)}
					{...shared}
				/>
			))}

			<div className="mt-3 px-1">
				<Button
					variant="outline"
					Icon={Plus}
					onClick={onAddArgument}
					data-testid="policy-add-argument"
				>
					Add custom argument
				</Button>
			</div>
		</>
	)
}

function SectionHeading({ children }: { children: ReactNode }) {
	return (
		<p className="px-1 pb-1 pt-3 text-xs font-medium text-tertiary first:pt-0">
			{children}
		</p>
	)
}
